import { useState, useEffect, useMemo } from 'react';
import { Live2DStage } from '../components/live2d';
import GameCheatPanel from '../components/game/GameCheatPanel';
import GameUiKitOverlay from '../game/ui/GameUiKitOverlay';
import GameSessionFlow from '../game/GameSessionFlow';
import { fetchGameConfig, fetchLocations } from '../game/gameSessionApi';

const MODEL_PATH = `${process.env.PUBLIC_URL || ''}/live2d/Lina/01_stand_face_NEW/01_stands_facing_us.model3.json`;

export default function GamePage() {
  const [cheatsOpen, setCheatsOpen] = useState(false);
  const [live2dCheatPreviewDockOpen, setLive2dCheatPreviewDockOpen] = useState(false);
  /** true во время интро / персонажного флоу: фоновый Live2D в main не монтируем (второй Cubism ломает рендер). */
  const [lobbyCharacterLive2dOpen, setLobbyCharacterLive2dOpen] = useState(false);
  /** Флоу может запретить фоновый Live2D (например Background slide). true = показывать. */
  const [mainLive2dVisible, setMainLive2dVisible] = useState(true);
  const [live2dSpeed, setLive2dSpeed] = useState(1);
  const [uiDemo, setUiDemo] = useState(null);
  const [gameSettings, setGameSettings] = useState(null);
  const [settingsError, setSettingsError] = useState(false);
  const [locationList, setLocationList] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const c = await fetchGameConfig();
        if (!cancelled && c) setGameSettings(c);
      } catch (e) {
        console.error(e);
        if (!cancelled) setSettingsError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchLocations();
        if (!cancelled) setLocationList(list);
      } catch (e) {
        console.error(e);
        if (!cancelled) setLocationList([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** API отдаёт локации с `createdAt: -1` (сначала новые). Для фона берём первую созданную запись. */
  const mainBackdropUrl = useMemo(() => {
    if (!locationList.length) return '';
    const sorted = [...locationList].sort((a, b) => {
      const ta = new Date(a.createdAt || 0).getTime();
      const tb = new Date(b.createdAt || 0).getTime();
      if (ta !== tb) return ta - tb;
      return String(a._id || '').localeCompare(String(b._id || ''));
    });
    const first = sorted[0];
    return String(first.imageUrl || first.thumbnailUrl || '').trim();
  }, [locationList]);

  const appReady = Boolean(gameSettings);

  const mainBackdropVisible =
    appReady &&
    !live2dCheatPreviewDockOpen &&
    !lobbyCharacterLive2dOpen &&
    mainLive2dVisible;

  const showLocationBackdrop = Boolean(mainBackdropUrl) && mainBackdropVisible;

  const showMainLive2d = mainBackdropVisible && !showLocationBackdrop;

  return (
    <>
      <div className="fixed inset-0 z-0 flex items-center justify-center bg-black">
        <div className="game-root relative aspect-video w-full max-w-[min(100vw,calc(100dvh*16/9))] overflow-hidden bg-game-canvas text-game-text [color-scheme:dark]">
          <div className="absolute inset-0">
            {gameSettings ? (
              <div className="absolute inset-0 z-[25] min-h-0">
                <GameSessionFlow
                  gameSettings={gameSettings}
                  onLobbyCharacterLive2dOverlay={setLobbyCharacterLive2dOpen}
                  onFlowLive2dHidden={setMainLive2dVisible}
                />
              </div>
            ) : null}

            {appReady ? (
              <div className="pointer-events-none absolute inset-0 z-[1]" aria-hidden>
                {showLocationBackdrop ? (
                  <img
                    src={mainBackdropUrl}
                    alt=""
                    className="absolute inset-0 block h-full w-full object-contain object-center"
                    draggable={false}
                    onContextMenu={(e) => e.preventDefault()}
                  />
                ) : null}
                {showMainLive2d ? (
                  <div className="absolute inset-0 flex h-full min-h-0 w-full flex-col">
                    <Live2DStage
                      className="h-full min-h-0 w-full flex-1"
                      modelUrl={MODEL_PATH}
                      motionGroup="idle"
                      motionIndex={0}
                      speedMultiplier={live2dSpeed}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}

            <GameUiKitOverlay demo={uiDemo} onClose={() => setUiDemo(null)} />

            <div className="absolute bottom-4 left-4 z-30 flex w-[5.75rem] flex-col gap-1">
              <GameCheatPanel
                open={cheatsOpen}
                onOpenChange={setCheatsOpen}
                live2dSpeed={live2dSpeed}
                onLive2dSpeedChange={setLive2dSpeed}
                onPickUiDemo={setUiDemo}
                onLive2dPreviewDockVisibilityChange={setLive2dCheatPreviewDockOpen}
              />
            </div>
          </div>
        </div>
      </div>

      {settingsError ? (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 p-6">
          <p className="text-center text-sm text-red-300">
            Could not load game settings. Check your connection and try again.
          </p>
        </div>
      ) : null}
      {!gameSettings && !settingsError ? (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90">
          <p className="text-sm text-game-muted">Loading…</p>
        </div>
      ) : null}
    </>
  );
}
